import React, { useMemo, useCallback } from 'react';
import { User, ListPlus, Home, Users, Settings, ChevronLeft, ChevronRight, BookOpen, FileText, Calendar, Edit, Printer, UserPlus, BookOpenCheck, ClipboardList, ChartNoAxesCombined, GraduationCap, Bot, TrendingUp, BookUser, Menu, X, Calculator, BarChart3, FileScan, BookMarked, UsersRound } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { Button } from './common';
import { HEX_COLORS } from '../styles/tokens';
import { V2_ROLES, OCR_ROLES, ACADEMY_ROLES } from '../utils/roles';
import { SHOW_ACADEMY_MENU, SHOW_V1_PROBLEM_MENUS } from '../utils/menuVisibility';

/**
 * 메뉴 이름 오른쪽에 붙는 버전 꼬리표(v2).
 * 이름보다 한 단 작고 회색이라, 활성 메뉴(초록)에서도 이름을 읽는 데 끼어들지 않는다.
 *
 * 접힘(90px)에서 여백이 1px 좁은 이유 — 실측(2026-08-31, 세로 스크롤바가 선 상태):
 * 버튼 안쪽 글자 자리가 39.0px 뿐인데 가장 긴 「시험지 v2」 가 여백 4px 이면 40.1px 로 1.1px 넘친다.
 * 3px 로 줄여 39.1px. 남는 0.1px 는 버튼 안쪽 여백(좌우 6px)이 받아 글자는 잘리지 않는다.
 * 스크롤바가 없을 때 글자 자리는 54.0px 라 어느 쪽이든 넉넉하다.
 *
 * @param {number} size 꼬리표 글자 크기(px) — 이름이 14px(펼침)이면 10, 10px(접힘)이면 9
 * @param {string} gap  이름과의 사이 여백 — 펼침 4px / 접힘 3px
 */
const badgeStyle = (size, gap = '4px') => ({
  fontSize: `${size}px`,
  fontWeight: 500,
  color: HEX_COLORS.gray400,
  marginLeft: gap,
});

function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userType } = useAuth();
  const { isExpanded, toggleSidebar } = useSidebar();

  // 전체 메뉴 아이템들 (새로운 순서와 그룹핑)
  const allMenuItems = useMemo(() => {
    const items = [
      // 첫 번째 그룹
      { icon: Home, label: '대시보드', path: '/dashboard', isActive: location.pathname === '/dashboard' },
      { icon: ChartNoAxesCombined, label: '폼레이팅', path: '/data', isActive: location.pathname.startsWith('/data') },
    ];
    
    // 통계는 선생님이 아닌 경우에만 추가 (관리자만)
    if (userType !== 'teacher') {
      items.push({ icon: BarChart3, label: '통계', path: '/statistics', isActive: location.pathname.startsWith('/statistics') });
    }
    
    items.push({ type: 'spacer' }); // 간격 추가
    
    // 두 번째 그룹 추가
    items.push(
      
      { icon: Users, label: '학생관리', path: '/student/1', isActive: (location.pathname.startsWith('/student') && !location.pathname.startsWith('/students-hub')) || location.pathname.startsWith('/test-papers/student') || location.pathname.startsWith('/test-result') || location.pathname.startsWith('/result') },
      { icon: TrendingUp, label: '학습분석', path: '/learning-status', isActive: location.pathname.startsWith('/learning-status') },
      { icon: FileText, label: '학습구성', path: '/testset', isActive: location.pathname.startsWith('/testset') || location.pathname.startsWith('/test-register') },
      { type: 'spacer' }, // 간격 추가
      
      { icon: GraduationCap, label: '시험분석', path: '/school-exams/analysis/1', isActive: location.pathname.startsWith('/school-exams') },
      { icon: Calendar, label: '시험지제작', path: '/preset', isActive: location.pathname.startsWith('/preset') }
    );

    // 네 번째 그룹 — 상수가 꺼져 있으면 앞 간격째 뺀다. 간격만 남기면 다음 간격과 겹쳐 구분선이 두 줄이 된다.
    if (SHOW_V1_PROBLEM_MENUS) {
      items.push(
        { type: 'spacer' }, // 간격 추가,
        { icon: Bot, label: '문제생성', path: '/problem-agent/page/1', isActive: location.pathname.startsWith('/problem-agent') },
        { icon: Edit, label: '문제관리', path: '/problem-edit/1', isActive: location.pathname.startsWith('/problem-edit') },
        { icon: Printer, label: '수동출력', path: '/problem-print', isActive: location.pathname.startsWith('/problem-print') }
      );
    }

    // 학습·문제·시험지(v2 페이지)는 매니저와 교사에게 연다 — 교사가 공유받은 폴더를 보려면
    // 그 화면에 들어올 수 있어야 한다. OCR 만 매니저 전용으로 남긴다(오너 결정 2026-09-09).
    // 허용 유형은 src/utils/roles.js 에서 열거로 판정한다(App.js 라우트 가드와 같은 출처).
    //
    // ── 메뉴명 이력 (2026-08-31 개명) ────────────────────────────────────────
    //   학생 학습관리 → 「학습」(/students-hub)
    //   문제임시     → 「문제」(/problem-hub)
    //   시험지 관리   → 「시험지」(/exam-papers)   ※ 레거시 /test-papers 의 빵부스러기
    //                                               「시험지 관리」는 다른 화면이라 그대로 둔다
    //   대규모 OCR   → 「OCR」(/ocr-hub)
    // 옛 이름은 여기 한 줄로만 남긴다. 다른 파일의 주석은 전부 새 이름 + 경로로 통일했으니
    // 화면을 찾을 때는 경로(`/students-hub` 같은)로 grep 한다 — 새 이름이 짧아
    // 「학습분석」·「학습구성」 같은 다른 메뉴와 글자가 섞이기 때문이다.
    // ────────────────────────────────────────────────────────────────────────
    if (V2_ROLES.includes(userType)) {
      // 스페이스로 구분 — 시험지제작(또는 v1 문제 메뉴) 아래, 관리 메뉴(사용자관리 · 매스컨셉) 위에 둔다
      items.push({ type: 'spacer' });

      // 학생(학생관리 v1 을 대체할 새 메뉴) — v2 묶음 맨 위(설계 D7). 학습·시험지 배정의 출발점이 학생이라 흐름에 맞다.
      // 표시 설정이 켜져 있고 ACADEMY_ROLES에 포함된 사용자에게 보인다.
      // 권한은 App.js 라우트 가드와 같은 출처(src/utils/roles.js). 주소가 /academy 인 이유는 App.js 주석.
      if (SHOW_ACADEMY_MENU && ACADEMY_ROLES.includes(userType)) {
        items.push({ icon: UsersRound, label: '학생', badge: 'v2', path: '/academy', isActive: location.pathname.startsWith('/academy') });
      }
      items.push({ icon: BookUser, label: '학습', badge: 'v2', path: '/students-hub', isActive: location.pathname.startsWith('/students-hub') });

      // 문제(문제관리+문제생성 통합) — 학습 아래.
      // 앞으로의 메인 문제 페이지(오너 지시): 문제관리 v2 + AI 변형 + 본문 이력이 여기로 모이고
      // 레거시(문제 관리·에이전트)는 정착 후 제거 예정.
      items.push({ icon: ListPlus, label: '문제', badge: 'v2', path: '/problem-hub', isActive: location.pathname.startsWith('/problem-hub') });

      // 시험지(신규 시험지·AI 복사 시험지)
      items.push({ icon: ClipboardList, label: '시험지', badge: 'v2', path: '/exam-papers', isActive: location.pathname.startsWith('/exam-papers') });

      // 강의(진도 순서로 짠 「문제 찾는 조건」의 나무) — 시험지 아래. 짜고 공유하는 곳은 여기 하나고,
      // 시험지 조립 창은 부르기만 한다(docs/learning-composition-v2/BE-20260911-reply-syllabus-preset.md).
      items.push({ icon: BookMarked, label: '강의', badge: 'v2', path: '/syllabi', isActive: location.pathname.startsWith('/syllabi') });

      // OCR(책 PDF → LLM OCR → 검수 → 문제은행 승격) — v2 묶음 맨 아래 배치.
      // 같은 무리 안에 있지만 문턱이 다르다 — 매니저에게만 보인다(교사 화면에는 위 넷만).
      if (OCR_ROLES.includes(userType)) {
        items.push({ icon: FileScan, label: 'OCR', badge: 'v2', path: '/ocr-hub', isActive: location.pathname.startsWith('/ocr-hub') });
      }
    }

    // 관리 메뉴(사용자관리 · 매스컨셉)는 v2 묶음 아래, 사이드바 맨 아래에 둔다(오너 지시 2026-10-22: v2 묶음을 관리 메뉴 위로).
    // 사용자관리는 선생님이 아닌 경우에만 추가
    if (userType !== 'teacher') {
      items.push({ type: 'spacer' }); // 간격 추가
      items.push({ icon: User, label: '사용자관리', path: '/user-management', isActive: location.pathname.startsWith('/user-management') || location.pathname.startsWith('/teachers') || location.pathname.startsWith('/managers') });
    }

    // 매스컨셉(iso-mathcepts)은 manager 전용 메뉴
    if (userType === 'manager') {
      items.push({ icon: Calculator, label: '매스컨셉', path: '/mathconcept', isActive: location.pathname.startsWith('/mathconcept') });
    }

    // 매스컨셉 메뉴를 제일 마지막에 추가
    // items.push({ type: 'spacer' }); // 간격 추가
    // items.push({ icon: Calculator, label: '매스컨셉', path: '/mathcepts-temp', isActive: location.pathname.startsWith('/mathcepts-temp') });
    
    return items;
  }, [location.pathname, userType]);

  // 네비게이션 핸들러
  const handleNavigation = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  // 사이드바 스타일 (플렉스 레이아웃의 일부)
  const sidebarStyle = {
    backgroundColor: 'white',
    color: '#1f2937',
    boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)',
    transition: 'all 300ms ease-in-out',
    width: isExpanded ? '180px' : '90px', // 200px에서 180px로 조정
    borderRadius: '0 0 24px 0', // 우측 상단 라운드 제거, 우측 하단만 유지
    height: '100%', // 전체 높이 사용
    overflow: 'hidden',
    flexShrink: 0,
    marginTop: '0', // 상단 여백 제거
    marginBottom: '0', // 하단 여백 제거
    marginLeft: 0, // 왼쪽 여백 제거
    // 라운드 영역 밖의 그림자 제거
    clipPath: 'inset(0 0 0 0 round 0 0 24px 0)',
  };

  // 토글 버튼 스타일
  const toggleButtonStyle = {
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 10,
    borderRadius: '50%',
    backgroundColor: '#f3f4f6',
    padding: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    transition: 'background-color 200ms',
    border: 'none',
  };

  return (
    <div style={sidebarStyle}>
      {/* 토글 버튼 - 하단 중앙 */}
      <button 
        onClick={toggleSidebar} 
        style={toggleButtonStyle}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
      >
        {isExpanded ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
      </button>

      {/* 메인 네비게이션 */}
      <nav style={{ 
        display: 'flex', 
        height: '100%', 
        flexDirection: 'column', 
        overflowY: 'auto', 
        paddingTop: '66px', // 헤더 높이(50px) + 여백(16px)
        paddingBottom: '60px' 
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '4px', 
          padding: '0 12px' 
        }}>
          {allMenuItems.map((item, index) => {
            // 간격 추가를 위한 spacer 렌더링
            if (item.type === 'spacer') {
              return <div key={index} style={{ height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: '60%', height: '1px', backgroundColor: '#f3f4f6' }} />
              </div>;
            }
            
            return (
              <button
                key={index}
                onClick={(e) => {
                  // 클릭 시 호버 효과 제거
                  e.currentTarget.style.backgroundColor = 'transparent';
                  // blur로 포커스 제거
                  e.currentTarget.blur();
                  handleNavigation(item.path);
                }}
                style={{
                  display: 'flex',
                  flexDirection: isExpanded ? 'row' : 'column',
                  alignItems: 'center',
                  gap: isExpanded ? '12px' : '4px',
                  borderRadius: '8px',
                  padding: '6px',
                  transition: 'background-color 200ms',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  if (!item.isActive) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div style={{
                  display: 'flex',
                  width: '30px',
                  height: '30px',
                  minWidth: '30px',
                  minHeight: '30px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  backgroundColor: item.isActive ? '#4ABC85' : '#f3f4f6',
                  transition: 'background-color 200ms',
                }}>
                  <item.icon 
                    size={20} 
                    style={{ color: item.isActive ? 'white' : '#6b7280' }}
                  />
                </div>
                
                {/* 확장 모드: 옆에 텍스트 */}
                {isExpanded && (
                  <span style={{
                    fontSize: '14px',
                    fontWeight: item.isActive ? 'bold' : '500',
                    color: item.isActive ? '#4ABC85' : '#374151',
                    whiteSpace: 'nowrap',
                  }}>
                    {item.label}
                    {item.badge && <span style={badgeStyle(10)}>{item.badge}</span>}
                  </span>
                )}
                
                {/* 축소 모드: 아래에 텍스트 (모바일 스타일) */}
                {!isExpanded && (
                  <span style={{
                    fontSize: '10px',
                    fontWeight: item.isActive ? 'bold' : '500',
                    color: item.isActive ? '#4ABC85' : '#6b7280',
                    whiteSpace: 'nowrap',
                  }}>
                    {item.label}
                    {item.badge && <span style={badgeStyle(9, '3px')}>{item.badge}</span>}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export default React.memo(Sidebar);
